import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Oval } from "react-loader-spinner";
import { Tooltip as ReactTooltip } from "react-tooltip";

import {
  Club,
  GlobalWrap,
  HeaderLabelText,
  PlayerColumnHeaderContent,
  PlayerColumnRowContent,
  PlayerFavoriteIcon,
  PlayerMediaContent,
  PlayerMediaPlaceholder,
  PlayerNameText,
  PlayerNumberText,
  PlayerPhoto,
  PlayerPositionText,
  PlayerStatsLoadingState,
  PlayerStatsScrollContainer,
  PlayerStatsSingleTable,
  PlayerTable,
} from "../../style/PlayersStat";
import placeholder from "../../images/ic_logo.svg";
import starOn from "../../images/star_on.svg";
import starOff from "../../images/star_off.svg";
import { $apiV1 } from "../../config/api";
import { useLexicon } from "../../hooks/useLexicon";
import { extractFirstCharacters } from "../../helpers";
import { openVideoList } from "../../helpers/service";

const getDefaultParameterDisplayName = (parameter) =>
  parameter?.parameter_name || parameter?.name || parameter?.title || "";

const getVisibleStatsMeta = (players = [], selectedIds = []) => {
  const statsById = new Map();

  (players || []).forEach((player) => {
    (player?.stats || []).forEach((stat) => {
      if (!statsById.has(stat.parameter_id)) {
        statsById.set(stat.parameter_id, stat);
      }
    });
  });

  const availableIds = [...statsById.keys()];

  if (availableIds.length === 0) {
    return [];
  }

  const matchingIds = selectedIds?.length
    ? availableIds.filter((id) => selectedIds.includes(id))
    : availableIds;

  const finalIds = matchingIds.length > 0 ? matchingIds : availableIds;

  return finalIds
    .sort((a, b) => a - b)
    .map((id) => statsById.get(id))
    .filter(Boolean);
};

const formatStatValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  if (Number.isInteger(value)) {
    return value.toLocaleString();
  }

  if (typeof value === "number") {
    return value.toFixed(1);
  }

  return value;
};

const GoalKeeperStat = ({
  items,
  club,
  tempSelectedFilters,
  gkPlayerSelectedFilters,
  loading = false,
  getParameterDisplayName = getDefaultParameterDisplayName,
}) => {
  const l = useLexicon();
  const navigate = useNavigate();
  const [favoritePlayers, setFavoritePlayers] = useState({});

  const filteredHomeTeam = useMemo(
    () => items?.home_team?.filter((player) => player.positions?.includes("GK")),
    [items?.home_team]
  );
  const filteredAwayTeam = useMemo(
    () => items?.away_team?.filter((player) => player.positions?.includes("GK")),
    [items?.away_team]
  );

  const visibleStatsMeta = useMemo(
    () =>
      getVisibleStatsMeta(
        [...(filteredHomeTeam || []), ...(filteredAwayTeam || [])],
        gkPlayerSelectedFilters?.checkedIds
      ),
    [filteredHomeTeam, filteredAwayTeam, gkPlayerSelectedFilters?.checkedIds]
  );

  // parameter_id -> stat, indexed per player id, so each cell lookup is O(1)
  // instead of a linear .find() over player.stats.
  const statByPlayerId = useMemo(() => {
    const byPlayer = new Map();
    const indexPlayer = (player) => {
      if (!player) return;
      const statMap = new Map();
      (player.stats || []).forEach((stat) => {
        if (!statMap.has(stat.parameter_id)) {
          statMap.set(stat.parameter_id, stat);
        }
      });
      byPlayer.set(player.id, statMap);
    };
    (filteredHomeTeam || []).forEach(indexPlayer);
    (filteredAwayTeam || []).forEach(indexPlayer);
    return byPlayer;
  }, [filteredHomeTeam, filteredAwayTeam]);

  const getStat = useCallback(
    (player, parameterId) => statByPlayerId.get(player?.id)?.get(parameterId),
    [statByPlayerId]
  );

  const handleImageError = (event) => {
    event.currentTarget.onerror = null;
    event.currentTarget.src = placeholder;
  };

  const toggleFavorite = async (player) => {
    try {
      const isCurrentlyFavorite = favoritePlayers[player.id] ?? false;

      if (isCurrentlyFavorite) {
        await $apiV1.delete(`/users/favorites/players/${player.id}/`);
      } else {
        await $apiV1.post(`/users/favorites/players/`, { player: player.id });
      }

      setFavoritePlayers((prev) => ({
        ...prev,
        [player.id]: !isCurrentlyFavorite,
      }));
    } catch (error) {
      console.error("Error toggling favorite status:", error);
    }
  };

  const renderStatCells = (player) =>
    visibleStatsMeta.map((headerStat) => {
      const stat = getStat(player, headerStat.parameter_id);
      const displayName = getParameterDisplayName(stat || headerStat);

      return (
        <td
          key={`${player.id}-${headerStat.parameter_id}`}
          className="statColumn"
          style={{ cursor: stat?.markers?.length ? "pointer" : "default" }}
          onClick={() => {
            if (stat?.markers?.length) {
              openVideoList(
                stat.markers.join(","),
                `${displayName} - ${player?.name} ${player?.surname}`
              );
            }
          }}
        >
          {formatStatValue(stat?.value)}
        </td>
      );
    });

  const renderTable = ({ teamName, players }) => (
    <PlayerTable>
      <Club style={{ marginTop: teamName === club?.away_team ? "20px" : undefined }}>
        <h2>{teamName}</h2>
      </Club>

      <PlayerStatsScrollContainer>
        <PlayerStatsSingleTable>
          <thead>
            <tr>
              <th className="playerColumn">
                <PlayerColumnHeaderContent>
                  <PlayerMediaPlaceholder />
                  <HeaderLabelText>#</HeaderLabelText>
                  <HeaderLabelText>{l(319)}</HeaderLabelText>
                  <HeaderLabelText>{l(11)}</HeaderLabelText>
                </PlayerColumnHeaderContent>
              </th>

              {visibleStatsMeta.map((stat) => {
                const displayName = getParameterDisplayName(stat);

                return (
                  <th
                    key={stat.parameter_id}
                    className="statColumn"
                    title={displayName}
                  >
                    {extractFirstCharacters(displayName)}
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {(players || []).map((player) => {
              const isFavorite = favoritePlayers[player.id] ?? false;

              return (
                <tr key={player.id}>
                  <td className="playerColumn">
                    <PlayerColumnRowContent>
                      <PlayerMediaContent>
                        <PlayerFavoriteIcon
                          src={isFavorite ? starOn : starOff}
                          alt="Favorite star"
                          onClick={() => toggleFavorite(player)}
                        />
                        <PlayerPhoto
                          src={player.photo ? player.photo : placeholder}
                          alt={player.display_name || `${player.name} ${player.surname}`}
                          onError={handleImageError}
                        />
                      </PlayerMediaContent>

                      <PlayerNumberText>{player.number}</PlayerNumberText>

                      <PlayerNameText
                        onClick={() => navigate(`/tournaments/player/${player.id}`)}
                        data-tooltip-id={`full-name-${teamName}-${player.id}`}
                        style={{ cursor: "pointer" }}
                      >
                        {player.display_name || `${player.name} ${player.surname}`}
                      </PlayerNameText>

                      <ReactTooltip
                        id={`full-name-${teamName}-${player.id}`}
                        place="bottom"
                        content={player.display_name || `${player.name} ${player.surname}`}
                      />

                      <PlayerPositionText>{player.positions?.[0]}</PlayerPositionText>
                    </PlayerColumnRowContent>
                  </td>

                  {renderStatCells(player)}
                </tr>
              );
            })}
          </tbody>
        </PlayerStatsSingleTable>
      </PlayerStatsScrollContainer>
    </PlayerTable>
  );

  if (loading) {
    const isLightTheme = localStorage.getItem("theme") === "light";

    return (
      <GlobalWrap>
        <PlayerStatsLoadingState>
          <Oval
            visible
            height="80"
            width="80"
            secondaryColor={isLightTheme ? "#4E5255" : "#CFDAE6"}
            color={isLightTheme ? "#2363F7" : "#4fa94d"}
            ariaLabel="oval-loading"
          />
        </PlayerStatsLoadingState>
      </GlobalWrap>
    );
  }

  return (
    <GlobalWrap>
      {renderTable({ teamName: club?.home_team, players: filteredHomeTeam })}
      {renderTable({ teamName: club?.away_team, players: filteredAwayTeam })}
    </GlobalWrap>
  );
};

export default GoalKeeperStat;
